import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Departments, DepartmentsDTO } from '../types/departments';
import { DepartmentsApiResponse } from '../types/departments-response';
import { DepartmentsHttpService } from './departments-http.service';
import { DepartmentsSnackbarsService } from './departments-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class DepartmentsService extends QaroniIntermediate {
  private departmentsSubject = new Subject<Departments[]>();
  private departmentSubject = new Subject<Departments>();

  private http = inject(DepartmentsHttpService);
  private commonsHttp = inject(CommonsHttpService);
  private snackbarService = inject(DepartmentsSnackbarsService);

  public getDepartmentsSubject$() {
    return this.departmentsSubject.asObservable().pipe(shareReplay(1));
  }

  public getDepartmentSubject$() {
    return this.departmentSubject.asObservable().pipe(shareReplay(1));
  }

  public list(companyID: number, params?: Params) {
    this.http
      .list$(companyID, params)
      .subscribe({ next: this.nextList, error: this.errorList });
  }

  private nextList = (data: DepartmentsApiResponse) => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      this.departmentsSubject.next(data.body.result);
      this.linksSubject.next(data.body.links);
    } else {
      this.departmentsSubject.next([]);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorList = () => {
    this.departmentsSubject.next([]);
  };

  public create(companyID: number, department: DepartmentsDTO) {
    this.http
      .create$(companyID, department)
      .subscribe({ next: this.nextCreate, error: this.errorCreate });
  }

  private nextCreate = (data: DepartmentsApiResponse) => {
    if (this.commonsHttp.validationsHttp.verifyStatus201(data)) {
      this.departmentSubject.next(data.body.result[0]);
      this.snackbarService.successRegistered();
    } else {
      this.departmentSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorCreate = () => {
    this.departmentSubject.next(null);
  };

  public get(companyID: number, departmentID: number) {
    this.http
      .get$(companyID, departmentID)
      .subscribe({ next: this.nextGet, error: this.errorGet });
  }

  private nextGet = (data: DepartmentsApiResponse) => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      this.departmentSubject.next(data.body.result[0]);
    } else {
      this.departmentSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorGet = () => {
    this.departmentSubject.next(null);
  };

  public update(companyID: number, department: DepartmentsDTO) {
    this.http
      .update$(companyID, department)
      .subscribe({ next: this.nextUpdate, error: this.errorUpdate });
  }

  private nextUpdate = (data: DepartmentsApiResponse) => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      this.departmentSubject.next(data.body.result[0]);
      this.snackbarService.successUpdated();
    } else {
      this.departmentSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorUpdate = () => {
    this.departmentSubject.next(null);
  };

  public duplicate(companyID: number, departmentID: number) {
    this.http
      .duplicate$(companyID, departmentID)
      .subscribe({ next: this.nextDuplicate, error: this.errorDuplicate });
  }

  private nextDuplicate = (data: DepartmentsApiResponse) => {
    if (this.commonsHttp.validationsHttp.verifyStatus201(data)) {
      this.departmentSubject.next(data.body.result[0]);
    } else {
      this.departmentSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorDuplicate = () => {
    this.departmentSubject.next(null);
  };

  public delete(companyID: number, departmentID: number) {
    this.http
      .delete$(companyID, departmentID)
      .subscribe({ next: this.nextRemove, error: this.errorRemove });
  }

  private nextRemove = (data: DepartmentsApiResponse) => {
    if (this.commonsHttp.validationsHttp.verifyStatus204(data)) {
      this.messageSubject.next('Department removed successfully');
    } else {
      this.messageSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorRemove = () => {
    this.messageSubject.next(null);
  };
}
